import React, { useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import useUserTokenStore from "@/stores/user-token.store";
import { logger } from "@/lib/logger";
import { useNavigate } from "react-router-dom";

export interface Claims {
  [key: string]: unknown; // Replace with a more specific type if known
}
export const claimsNamespace = "https://markup.legal";

const withClaimCheck = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  claimKey: string,
  claimValue?: string | boolean,
  routeToNotAuthorized?: string
) => {
  return (props: P) => {
    const navigate = useNavigate();
    const namespace = "https://markup.legal/";
    const { token } = useUserTokenStore();

    const checkClaim = async () => {
      try {
        if (!token) {
          if (routeToNotAuthorized) {
            navigate(routeToNotAuthorized, { replace: true });
          }
          return;
        }

        const decodedToken: Claims = jwtDecode(token);
        const claimMatches =
          claimValue !== undefined
            ? decodedToken[namespace + claimKey] === claimValue
            : claimKey in decodedToken;

        if (!claimMatches && routeToNotAuthorized) {
          navigate(routeToNotAuthorized, { replace: true });
        }
      } catch (error) {
        logger.error("Failed to decode the access token for a claim check", {
          operation: "checkClaim",
          error,
          claimKey,
        });
        if (routeToNotAuthorized) {
          navigate(routeToNotAuthorized, { replace: true });
        }
      }
    };

    useEffect(() => {
      checkClaim();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, claimKey, claimValue, routeToNotAuthorized]);

    try {
      if (!token) {
        return null;
      }

      const decodedToken: Claims = jwtDecode(token);
      const claimMatches =
        claimValue !== undefined
          ? decodedToken[`${claimsNamespace}/${claimKey}`] === claimValue
          : claimKey in decodedToken;

      if (claimMatches) {
        return <WrappedComponent {...(props as P)} />;
      }

      return null;
    } catch {
      // checkClaim (the effect above) logs this failure; logging here too
      // would repeat it on every render.
      return null;
    }
  };
};

export default withClaimCheck;
